import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useAuthRefresh } from '../lib/useAuthRefresh';

const FormBuilderContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const FormBuilderProvider = ({ children, token }) => {
  // Silent access-token refresh — see useAuthRefresh for the rationale.
  useAuthRefresh(token, { backendUrl: API_URL, apiPrefix: '/api/form-builder' });

  const [forms, setForms] = useState([]);
  const [currentForm, setCurrentForm] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [isReady, setIsReady] = useState(false);

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  useEffect(() => {
    if (token) {
      setIsReady(true);
    }
  }, [token]);

  // ==================== FORM OPERATIONS ====================

  const fetchForms = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (params.status) queryParams.append('status', params.status);
      if (params.search) queryParams.append('search', params.search);
      if (params.page) queryParams.append('page', params.page);
      if (params.page_size) queryParams.append('page_size', params.page_size);

      const response = await axios.get(
        `${API_URL}/api/form-builder/forms?${queryParams.toString()}`,
        { headers }
      );
      setForms(response.data.forms || []);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch forms:', err);
      setError(err.response?.data?.detail || 'Failed to fetch forms');
      return { forms: [], pagination: {} };
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchForm = useCallback(async (formId) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/form-builder/forms/${formId}`,
        { headers }
      );
      setCurrentForm(response.data);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch form:', err);
      setError(err.response?.data?.detail || 'Failed to fetch form');
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  const createForm = useCallback(async (formData) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/form-builder/forms`,
        formData,
        { headers }
      );
      setForms(prev => [response.data, ...prev]);
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Failed to create form:', err);
      return { success: false, error: err.response?.data?.detail || 'Failed to create form' };
    } finally {
      setLoading(false);
    }
  }, [token]);

  const updateForm = useCallback(async (formId, formData) => {
    setLoading(true);
    try {
      const response = await axios.put(
        `${API_URL}/api/form-builder/forms/${formId}`,
        formData,
        { headers }
      );
      setForms(prev => prev.map(f => f.id === formId ? response.data : f));
      setCurrentForm(response.data);
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Failed to update form:', err);
      return { success: false, error: err.response?.data?.detail || 'Failed to update form' };
    } finally {
      setLoading(false);
    }
  }, [token]);

  const deleteForm = useCallback(async (formId) => {
    setLoading(true);
    try {
      await axios.delete(
        `${API_URL}/api/form-builder/forms/${formId}`,
        { headers }
      );
      setForms(prev => prev.filter(f => f.id !== formId));
      return { success: true };
    } catch (err) {
      console.error('Failed to delete form:', err);
      return { success: false, error: err.response?.data?.detail || 'Failed to delete form' };
    } finally {
      setLoading(false);
    }
  }, [token]);

  const cloneForm = useCallback(async (formId) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/form-builder/forms/${formId}/clone`,
        {},
        { headers }
      );
      setForms(prev => [response.data, ...prev]);
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Failed to clone form:', err);
      return { success: false, error: err.response?.data?.detail || 'Failed to clone form' };
    } finally {
      setLoading(false);
    }
  }, [token]);

  const updateFormStatus = useCallback(async (formId, status) => {
    try {
      await axios.put(
        `${API_URL}/api/form-builder/forms/${formId}/status?status=${status}`,
        {},
        { headers }
      );
      setForms(prev => prev.map(f => f.id === formId ? { ...f, status } : f));
      if (currentForm?.id === formId) {
        setCurrentForm(prev => ({ ...prev, status }));
      }
      return { success: true };
    } catch (err) {
      console.error('Failed to update form status:', err);
      return { success: false, error: err.response?.data?.detail || 'Failed to update status' };
    }
  }, [token, currentForm]);

  // ==================== RESPONSE OPERATIONS ====================

  const fetchResponses = useCallback(async (formId, params = {}) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (params.search) queryParams.append('search', params.search);
      if (params.page) queryParams.append('page', params.page);
      if (params.page_size) queryParams.append('page_size', params.page_size);

      const response = await axios.get(
        `${API_URL}/api/form-builder/forms/${formId}/responses?${queryParams.toString()}`,
        { headers }
      );
      setResponses(response.data.responses || []);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch responses:', err);
      setError(err.response?.data?.detail || 'Failed to fetch responses');
      return { responses: [], pagination: {} };
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchResponse = useCallback(async (formId, responseId) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/form-builder/forms/${formId}/responses/${responseId}`,
        { headers }
      );
      return response.data;
    } catch (err) {
      console.error('Failed to fetch response:', err);
      return null;
    }
  }, [token]);

  const updateResponse = useCallback(async (formId, responseId, answers) => {
    try {
      const response = await axios.put(
        `${API_URL}/api/form-builder/forms/${formId}/responses/${responseId}`,
        { answers },
        { headers }
      );
      setResponses(prev => prev.map(r => r.id === responseId ? response.data : r));
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Failed to update response:', err);
      return { success: false, error: err.response?.data?.detail || 'Failed to update response' };
    }
  }, [token]);

  const deleteResponse = useCallback(async (formId, responseId) => {
    try {
      await axios.delete(
        `${API_URL}/api/form-builder/forms/${formId}/responses/${responseId}`,
        { headers }
      );
      setResponses(prev => prev.filter(r => r.id !== responseId));
      return { success: true };
    } catch (err) {
      console.error('Failed to delete response:', err);
      return { success: false, error: err.response?.data?.detail || 'Failed to delete response' };
    }
  }, [token]);

  const exportResponses = useCallback(async (formId) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/form-builder/forms/${formId}/export`,
        { headers }
      );
      return response.data;
    } catch (err) {
      console.error('Failed to export responses:', err);
      return null;
    }
  }, [token]);

  // ==================== DASHBOARD ====================

  const fetchDashboardStats = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/form-builder/dashboard/stats`,
        { headers }
      );
      setDashboardStats(response.data);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch dashboard stats:', err);
      return null;
    }
  }, [token]);

  // ==================== AUDIT LOGS ====================

  const fetchAuditLogs = useCallback(async (page = 1, pageSize = 50, entity = null, action = null) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('page', page);
      queryParams.append('page_size', pageSize);
      if (entity) queryParams.append('entity', entity);
      if (action) queryParams.append('action', action);

      const response = await axios.get(
        `${API_URL}/api/form-builder/audit-logs?${queryParams.toString()}`,
        { headers }
      );
      return response.data;
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
      return { logs: [], pagination: {} };
    }
  }, [token]);

  const value = {
    // State
    forms,
    currentForm,
    responses,
    loading,
    error,
    dashboardStats,
    isReady,
    // Form operations
    fetchForms,
    fetchForm,
    createForm,
    updateForm,
    deleteForm,
    cloneForm,
    updateFormStatus,
    setCurrentForm,
    // Response operations
    fetchResponses,
    fetchResponse,
    updateResponse,
    deleteResponse,
    exportResponses,
    // Dashboard
    fetchDashboardStats,
    // Audit
    fetchAuditLogs,
    // Utils
    setError
  };

  return (
    <FormBuilderContext.Provider value={value}>
      {children}
    </FormBuilderContext.Provider>
  );
};

export const useFormBuilder = () => {
  const context = useContext(FormBuilderContext);
  if (!context) {
    throw new Error('useFormBuilder must be used within a FormBuilderProvider');
  }
  return context;
};

export default FormBuilderContext;
